/**
 * Community Garden — WebSocket Client
 *
 * Connects to the relay server and emits typed callbacks
 * for garden events.
 */

import { authService } from './auth.js';
import { SecureTokenStorage } from './token-storage.js';
import { errorTracker } from './error-tracking.js';
import { analytics } from './analytics.js';
import { showToast } from './toasts.js';

/** GARDEN-075: Pet system data - GARDEN-144: Added version/timestamp for stale data detection */
export interface PetData {
  totalLoginDays: number;
  lastLoginDayKey: string | null;
  petUnlocked: boolean;
  petType: string | null;
  petVariant: string | null;
  petStatus: string | null;
  petFedDayKey: string | null;
  lastFedDayKey: string | null;
  petFeedStreak: number;
  /** Daily login streak counter */
  loginStreak?: number;
  /** GARDEN-087: Bond level for companion */
  bondLevel?: number;
  /** GARDEN-144: Monotonic version for detecting stale data */
  petVersion?: number;
  /** GARDEN-144: Server timestamp when this data was generated */
  timestamp?: number;
}

/** Stage progress info from server */
export interface StageProgress {
  currentStage: number;
  currentStageName: string;
  nextStage: number | null;
  nextStageName: string | null;
  prevThreshold: number;
  nextThreshold: number | null;
  totalPoints: number;
  progress: number;  // 0..1
  isMax: boolean;
  totalStages: number;
}

/** User rank progress info from server */
export interface RankProgress {
  mode: "threshold";
  userPoints: number;
  rankTier: string;
  rankTitle: string;
  rankColor: string;
  workRole?: string;
  workRoleDescription?: string;
  workAction?: CommunityJobAction;
  nextRankTitle?: string;
  nextRankThreshold?: number;
  prevRankThreshold?: number;
  /** 0..1 normalised progress */
  progress: number;
  isMax: boolean;
}

export type CommunityJobAction =
  | "tend"
  | "water"
  | "plant"
  | "decorate"
  | "chat"
  | "read"
  | "observe"
  | "celebrate";

export type CommunityProjectFeature =
  | "starter_beds"
  | "flower_border"
  | "path_lanterns"
  | "pond_reeds"
  | "gazebo_bunting"
  | "festival_banner"
  | "market_crates"
  | "crystal_chimes"
  | "star_marker"
  | "orchard_baskets"
  | "aurora_totem"
  | "celestial_shrine"
  | "meadow_arch"
  | "paradise_fountain"
  | "infinity_beacon"
  | "legend_standard";

export interface CommunityJobParticipant {
  userId: string;
  userName: string;
  rankTier: string;
  rankTitle: string;
  role: string;
  roleDescription: string;
  action: CommunityJobAction;
}

export interface CommunityProjectOutcome {
  id: string;
  title: string;
  outcome: string;
  feature: CommunityProjectFeature;
  x: number;
  y: number;
  completedAt: number;
  contributors: CommunityJobParticipant[];
}

export interface CommunityWorkState {
  activeJob: {
    id: string;
    stage: number;
    cycle: number;
    title: string;
    description: string;
    outcome: string;
    feature: CommunityProjectFeature;
    targetX: number;
    targetY: number;
    progress: number;
    goal: number;
    participants: CommunityJobParticipant[];
    updatedAt: number;
  };
  outcomes: CommunityProjectOutcome[];
  recentCompletion?: CommunityProjectOutcome;
}

export interface InitPayload {
  tiles: TileData[][];
  users: Record<string, UserData>;
  totalPoints: number;
  stage: number;
  communityWork?: CommunityWorkState;
  /** Relationship pairs for ambient couple animations */
  couplePairs?: Array<[string, string]>;
  props?: Prop[];
  userId?: string | null;
  stageProgress?: StageProgress;
  rankProgress?: RankProgress | null;
  /** GARDEN-047: visual theme identifier (defaults to "garden") */
  themeId?: string;
  /** GARDEN-075: Pet system data */
  petData?: PetData;
  /** GARDEN-135: Pet states for all users in the garden */
  petStates?: Record<string, PetData>;
  /** Authenticated user profile (sent by server on user-type connections) */
  user?: {
    id: string;
    discord_id: string;
    username: string;
    avatar: string | null;
    discriminator: string;
    /** Account-wide lifetime total. Garden rank never uses this value. */
    points: number;
    accountPoints?: number;
    contributions: number;
  } | null;
}

export interface TileData {
  level: "soil" | "sprout" | "flower" | "lush";
  hasPath: boolean;
  hasWater: boolean;
  hasDecor: boolean;
  hue: number;
  /** Stage 4+: tree type (none, oak, pine, maple, cherry, willow) */
  treeType: "none" | "oak" | "pine" | "maple" | "cherry" | "willow";
  /** Stage 4+: has gazebo structure */
  hasGazebo: boolean;
  /** Stage 4+: tree seasonal variant (0-3 for spring, summer, fall, winter) */
  season: number;
  /** Stage 6+: procedural structure marker */
  structureType?:
    | "none"
    | "windmill"
    | "cottage"
    | "market_stall"
    | "greenhouse"
    | "clock_tower"
    | "chicken_coop"
    | "crystal_formation"
    | "mushroom_ring"
    | "enchanted_flower"
    | "observatory"
    | "wishing_well"
    | "fruit_tree_apple"
    | "fruit_tree_cherry"
    | "fruit_tree_peach"
    | "cherry_blossom_tree"
    | "topiary"
    | "grand_arch"
    | "floating_island"
    | "glowing_mushroom";
  /** Stage 6+: special ground-zone marker */
  zone?: "none" | "village_square" | "cobblestone_square" | "crystal_path" | "orchard" | "aurora_field";
  /** Most recent real Discord activity that tended this tile. */
  lastTendedAt?: number;
}

export interface UserData {
  id: string;
  name: string;
  isBot: boolean;
  x: number;
  y: number;
  points: number;
  rankTier?: string;
  rankColor?: string;
  rankTitle?: string;
  avatar?: {
    avatarSkinToneId: string;
    avatarHairId: string;
    avatarHairStyleId?: string | null;
    avatarTopColorId: string;
    avatarBottomColorId: string;
    equippedSpecialOutfitId?: string | null;
  };
}

export interface Prop {
  id: string;
  x: number;
  y: number;
  propType: "lantern" | "bench" | "birdhouse" | "planter";
  placedBy: string;
  placedAt: number;
}

export interface ActivityPayload {
  userId: string;
  userName: string;
  userIsBot?: boolean;
  text: string;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  tileX: number;
  tileY: number;
  tileNewLevel: "soil" | "sprout" | "flower" | "lush";
  /** Complete changed tile for live growth and feature updates. */
  tile?: TileData;
  totalPoints: number;
  stage: number;
  userPoints: number;
  /** action type classification */
  actionType: "tend" | "plant" | "bloom" | "decorate";
  /** reply meet-up info */
  replyToId?: string;
  replyToName?: string;
  replyToX?: number;
  replyToY?: number;
  /** prop placed for decorate action */
  placedProp?: Prop;
  /** points awarded for this single action (delta, not cumulative) */
  pointsDelta?: number;
  /** present when this activity advanced the garden stage */
  stageUnlockedFrom?: number;
  /** full garden tiles when a stage unlock mutates global features */
  tiles?: TileData[][];
  /** rank display fields (from GARDEN-029) */
  rankTier?: string;
  rankColor?: string;
  rankTitle?: string;
  /** progress bar data (GARDEN-037) */
  stageProgress?: StageProgress;
  rankProgress?: RankProgress | null;
  communityWork?: CommunityWorkState;
}

export interface ScorePayload {
  userId: string;
  userName?: string;
  totalPoints: number;
  userPoints: number;
  stage: number;
  /** points awarded for this single action (delta, not cumulative) */
  pointsDelta?: number;
  /** present when this score event advanced the garden stage */
  stageUnlockedFrom?: number;
  /** full garden tiles when a stage unlock mutates global features */
  tiles?: TileData[][];
  /** action type classification */
  actionType: "tend" | "plant" | "bloom" | "decorate" | "quest";
  source?: "reaction" | "quest" | "quest_backfill";
  rankTier?: string;
  rankTitle?: string;
  rankColor?: string;
  topGardeners?: Array<{
    userId: string;
    username: string;
    points: number;
    rankTier: string;
    rankTitle: string;
    rankColor: string;
  }>;
  /** progress bar data (GARDEN-037) */
  stageProgress?: StageProgress;
  rankProgress?: RankProgress | null;
  communityWork?: CommunityWorkState;
}

/** GARDEN-046: Avatar updated payload */
export interface AvatarUpdatedPayload {
  userId: string;
  avatar: {
    avatarSkinToneId: string;
    avatarHairId: string;
    avatarHairStyleId?: string | null;
    avatarTopColorId: string;
    avatarBottomColorId: string;
    equippedSpecialOutfitId?: string | null;
  };
}

/** GARDEN-142: Discord avatar update payload */
export interface DiscordAvatarUpdatedPayload {
  userId: string;
  avatarHash: string | null;
}

/** GARDEN-047, GARDEN-143: Theme changed payload - broadcast to all clients when admin changes theme */
export interface ThemeChangedPayload {
  themeId: string;
}

/** GARDEN-127: Pet state update payload - GARDEN-144: Batched events support */
export interface PetStateUpdatePayload {
  petData: PetData;
  timestamp: number;
  /** GARDEN-144: Batch ID for grouping related pet events */
  batchId?: string;
  /** GARDEN-144: Whether this is the final event in a batch */
  batchComplete?: boolean;
}

/** GARDEN-127: Pet fed payload */
export interface PetFedPayload {
  userId: string;
  petData: PetData;
  streak: number;
}

/** GARDEN-127: Pet adopted payload */
export interface PetAdoptedPayload {
  userId: string;
  petType: string;
  petVariant: string;
}

/** GARDEN-127: Pet wandered payload */
export interface PetWanderedPayload {
  userId: string;
  message: string;
}

export interface NotificationPayload {
  notification: {
    id: string;
    user_id: string;
    type: 'garden_visit' | 'garden_like' | 'garden_comment' | 'rank_change';
    related_user_id: string | null;
    content: string | null;
    read: boolean;
    created_at: string;
  };
}

export interface AccountPointsPayload {
  accountPoints: number;
}

export type EventHandlers = {
  onInit: (data: InitPayload) => void;
  onActivity: (data: ActivityPayload) => void;
  onScore: (data: ScorePayload) => void;
  onAvatarUpdated?: (data: AvatarUpdatedPayload) => void;
  onThemeChanged?: (data: ThemeChangedPayload) => void;
  onPetStateUpdate?: (data: PetStateUpdatePayload) => void;
  onPetFed?: (data: PetFedPayload) => void;
  onPetAdopted?: (data: PetAdoptedPayload) => void;
  onPetWandered?: (data: PetWanderedPayload) => void;
  onNotification?: (data: NotificationPayload) => void;
  onAccountPoints?: (data: AccountPointsPayload) => void;
  /** GARDEN-142: Discord avatar update from server */
  onDiscordAvatarUpdated?: (data: DiscordAvatarUpdatedPayload) => void;
  onConnect: () => void;
  onDisconnect: () => void;
  onReconnecting?: () => void;
  /** SCOUT-089: Called when auto-reconnect gives up after max retries */
  onFailed?: () => void;
};

export class GardenWSClient {
  private ws: WebSocket | null = null;
  private url: string;
  private handlers: EventHandlers;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private guildId: string | null = null;
  /** GARDEN-153: Track if this is a reconnect to trigger full state refresh */
  private isReconnect = false;
  /** GARDEN-130: Track connection timing */
  private connectionStartTime: number = 0;
  /** GARDEN-137: Track reconnect count for analytics */
  private reconnectCount: number = 0;
  /** GARDEN-169: Exponential backoff retry tracking */
  private retryAttempt = 0;
  /** SCOUT-026: Set when the server sends an auth error — suppresses auto-reconnect */
  private authError = false;
  private readonly MAX_RETRY_ATTEMPTS = 10;
  private readonly BASE_RETRY_DELAY_MS = 1000;
  private readonly MAX_RETRY_DELAY_MS = 30000;
  private destroyed = false;

  constructor(url: string, handlers: EventHandlers, guildId?: string) {
    this.url = url;
    this.handlers = handlers;
    this.guildId = guildId || null;
  }

  connect() {
    if (this.destroyed) return;
    this.connectionStartTime = performance.now();
    try {
      this.ws = new WebSocket(this.url);
    } catch (error) {
      errorTracker.trackWsEvent('error');
      errorTracker.captureError(error instanceof Error ? error : new Error('WebSocket connection failed'));
      this.scheduleReconnect();
      return;
    }

    this.ws.onopen = async () => {
      const duration = performance.now() - this.connectionStartTime;
      if (import.meta.env.DEV) {
        console.log("[ws] connected");
      }
      errorTracker.trackWsEvent(this.isReconnect ? 'reconnect' : 'connect', duration);
      // GARDEN-169: Reset retry attempt on successful connection
      this.retryAttempt = 0;
      // Reset reconnect count for analytics to track per-session bursts
      this.reconnectCount = 0;

      // Get auth token. The in-memory token is empty in tabs whose session was
      // restored from the httpOnly cookie (memory storage is per-tab and
      // cleared on reload), which silently downgraded logged-in users to
      // anonymous viewers — for private gardens the server then rejected the
      // connection and the HUD fell back to cached/default state. Fall back to
      // the async cookie-backed refresh before giving up on user identity.
      let token = SecureTokenStorage.getAccessToken();
      if (!token && authService.isAuthenticated()) {
        try {
          token = await SecureTokenStorage.getValidToken();
        } catch {
          token = null;
        }
        // Socket may have closed while we awaited the refresh.
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
          return;
        }
      }

      // identify as user if authenticated, otherwise as viewer
      // GARDEN-153: Include reconnect flag to trigger full state refresh
      const initPayload = {
        type: "viewer" as const,
        guildId: this.guildId,
        timestamp: Date.now(),
        reconnect: this.isReconnect
      };
      if (token && authService.isAuthenticated()) {
        this.ws!.send(JSON.stringify({
          ...initPayload,
          type: "user" as const,
          token: token
        }));
      } else {
        this.ws!.send(JSON.stringify(initPayload));
      }
      // GARDEN-153: Reset reconnect flag after connection established
      this.isReconnect = false;
      this.handlers.onConnect();
    };

    this.ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data as string);
        switch (msg.type) {
          case "init":
            errorTracker.trackMilestone('ws_connected', { guildId: this.guildId });
            errorTracker.trackMilestone('garden_view', { guildId: this.guildId });
            this.handlers.onInit(msg as InitPayload);
            break;
          case "activity":
            this.handlers.onActivity(msg as ActivityPayload);
            break;
          case "score":
            this.handlers.onScore(msg as ScorePayload);
            break;
          case "avatar_updated":
            this.handlers.onAvatarUpdated?.(msg as AvatarUpdatedPayload);
            break;
          case "discord_avatar_updated":
            // GARDEN-142: Update avatar in AuthService (single source of truth)
            const avatarMsg = msg as DiscordAvatarUpdatedPayload;
            authService.updateAvatar(avatarMsg.avatarHash);
            this.handlers.onDiscordAvatarUpdated?.(avatarMsg);
            break;
          case "theme_changed":
            this.handlers.onThemeChanged?.(msg as ThemeChangedPayload);
            break;
          case "pet_state_update":
            this.handlers.onPetStateUpdate?.(msg as PetStateUpdatePayload);
            break;
          case "pet_fed":
            this.handlers.onPetFed?.(msg as PetFedPayload);
            break;
          case "pet_adopted":
            this.handlers.onPetAdopted?.(msg as PetAdoptedPayload);
            break;
          case "pet_wandered":
            this.handlers.onPetWandered?.(msg as PetWanderedPayload);
            break;
          case "notification":
            this.handlers.onNotification?.(msg as NotificationPayload);
            break;
          case "account_points":
            this.handlers.onAccountPoints?.(msg as AccountPointsPayload);
            break;
          case "error": {
            const serverMsg: string = (msg as { type: string; message?: string }).message || 'Connection error';
            const isAuthError = /token|auth|session|login|member/i.test(serverMsg);
            if (isAuthError) {
              this.authError = true;
              showToast({
                message: 'Session expired or invalid. Please log in again.',
                variant: 'error',
                duration: 0,
                dedupeKey: 'ws-auth-error',
                action: {
                  label: 'Log in',
                  onClick: () => authService.loginWithDiscord(),
                },
              });
            } else {
              showToast({
                message: `Connection error: ${serverMsg}`,
                variant: 'error',
                duration: 6000,
                dedupeKey: `ws-error:${serverMsg}`,
              });
            }
            break;
          }
        }
      } catch (err) {
        console.warn("[ws] parse error", err);
      }
    };

    this.ws.onclose = () => {
      if (this.destroyed) return;
      if (import.meta.env.DEV) console.log("[ws] disconnected");
      errorTracker.trackWsEvent('disconnect');
      analytics.track('ws_disconnect', {
        guild_id: this.guildId,
        reconnect_count: this.reconnectCount,
      });
      this.handlers.onDisconnect();
      this.scheduleReconnect();
    };

    this.ws.onerror = (error) => {
      if (this.destroyed) return;
      errorTracker.trackWsEvent('error');
      errorTracker.captureError(new Error('WebSocket error'), { guildId: this.guildId });
      analytics.track('ws_error', {
        guild_id: this.guildId,
        reconnect_count: this.reconnectCount,
      });
      this.ws?.close();
    };
  }

  private scheduleReconnect() {
    if (this.destroyed) return;
    if (this.reconnectTimer) return;
    // SCOUT-026: Don't auto-reconnect after an auth error — user must log in first
    if (this.authError) return;

    // GARDEN-169: Check max retry attempts
    if (this.retryAttempt >= this.MAX_RETRY_ATTEMPTS) {
      console.error(`[ws] Max retry attempts (${this.MAX_RETRY_ATTEMPTS}) exceeded. Giving up.`);
      errorTracker.captureError(new Error('WebSocket max retry attempts exceeded'));
      this.handlers.onFailed?.();
      return;
    }

    this.handlers.onReconnecting?.();
    this.isReconnect = true; // GARDEN-153: Mark as reconnect to trigger full state refresh
    this.reconnectCount++;

    // GARDEN-169: Calculate exponential backoff delay: min(1000 * 2^attempt, 30000)
    const delay = Math.min(
      this.BASE_RETRY_DELAY_MS * Math.pow(2, this.retryAttempt),
      this.MAX_RETRY_DELAY_MS
    );
    this.retryAttempt++;

    if (import.meta.env.DEV) {
      console.log(`[ws] Reconnecting in ${delay}ms (attempt ${this.retryAttempt}/${this.MAX_RETRY_ATTEMPTS})`);
    }

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  /** GARDEN-061: Manual reconnect triggered by user clicking Reconnect button */
  reconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.isReconnect = true; // GARDEN-153: Mark as reconnect to trigger full state refresh
    // GARDEN-169: Reset retry attempt on manual reconnect
    this.retryAttempt = 0;
    // SCOUT-026: Reset auth error flag on manual reconnect
    this.authError = false;
    this.ws?.close();
    this.connect();
  }

  /** GARDEN-130: Update guild ID and error tracking context */
  setGuildId(guildId: string): void {
    this.guildId = guildId;
    const user = authService.getState().user;
    errorTracker.setContext(guildId, user?.id);
  }

  destroy() {
    this.destroyed = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onerror = null;
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }
  }
}
